import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { getOrganizations } from "../services/admin";
import type { OrganizationSummary } from "../types/admin";
import { ArrowIcon } from "../assets/ArrowIcon";
import { ModalCloseButton } from "./ModalCloseButton";
import "./OrganizationSwitcher.css";

export const OrganizationSwitcher = () => {
  const {
    user,
    activeOrganization,
    isSystemAdmin,
    isImpersonating,
    switchOrganization,
    clearImpersonation,
  } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Determine current org name
  const currentOrgName =
    isImpersonating && activeOrganization
      ? activeOrganization.name
      : user?.profile?.organizationName || "Unknown";

  const fetchOrganizations = useCallback(async () => {
    if (!isSystemAdmin) return;

    setLoading(true);
    try {
      const response = await getOrganizations({
        pageSize: 50,
        search: searchTerm || undefined,
      });
      setOrganizations(response.items);
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    } finally {
      setLoading(false);
    }
  }, [isSystemAdmin, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen, fetchOrganizations]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSelectOrg = (org: OrganizationSummary) => {
    switchOrganization({
      id: org.id,
      name: org.name,
      sport: org.sport,
    });
    handleClose();
  };

  const handleReturnHome = () => {
    clearImpersonation();
    handleClose();
  };

  // Don't render if not system admin
  if (!isSystemAdmin) {
    return null;
  }

  return (
    <div className="org-switcher">
      <button
        type="button"
        className={`org-switcher__trigger ${
          isImpersonating ? "org-switcher__trigger--impersonating" : ""
        }`}
        onClick={handleToggle}
        aria-label="Switch organization"
      >
        <span className="org-switcher__label">
          {isImpersonating && (
            <span className="org-switcher__badge">Viewing</span>
          )}
          <span className="org-switcher__org-name">{currentOrgName}</span>
        </span>
        <ArrowIcon className="org-switcher__arrow" />
      </button>

      {isOpen && (
        <>
          <div className="org-switcher__overlay" onClick={handleClose} />
          <div className="org-switcher__dropdown">
            <div className="org-switcher__header">
              <h3 className="org-switcher__title">Switch Organization</h3>
              <ModalCloseButton onClick={handleClose} />
            </div>

            <div className="org-switcher__search">
              <input
                type="text"
                className="org-switcher__search-input"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            {isImpersonating && (
              <button
                type="button"
                className="org-switcher__home-btn"
                onClick={handleReturnHome}
              >
                Return to {user?.profile?.organizationName}
              </button>
            )}

            <div className="org-switcher__list">
              {loading ? (
                <div className="org-switcher__loading">Loading...</div>
              ) : organizations.length === 0 ? (
                <div className="org-switcher__empty">
                  No organizations found
                </div>
              ) : (
                organizations.map((org) => {
                  const isSelected = isImpersonating
                    ? activeOrganization?.id === org.id
                    : user?.profile?.organizationId === org.id;

                  return (
                    <button
                      key={org.id}
                      type="button"
                      className={`org-switcher__item ${
                        isSelected ? "org-switcher__item--selected" : ""
                      }`}
                      onClick={() => handleSelectOrg(org)}
                    >
                      <div className="org-switcher__item-info">
                        <span className="org-switcher__item-name">
                          {org.name}
                        </span>
                        {org.sport && (
                          <span className="org-switcher__item-sport">
                            {org.sport}
                          </span>
                        )}
                      </div>
                      <span className="org-switcher__item-stats">
                        {org.userCount} users, {org.prospectCount} prospects
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
